import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';
import http from 'http';
import https from 'https';

import { getJwtFromCookie } from 'utils/jwtCookie';

/**
 * The shared axiosInstance
 */
let axiosInstance: AxiosInstance | undefined;

/**
 * The HTTP Client settings to initialize it with.
 * @property options AxiosRequestConfig optional standard Axios configuration options
 * @property salonSlug string The optional salon slug to initialize the client for (used server side)
 */
export type ClientSettings = {
  options?: AxiosRequestConfig;
};

/**
 * Gets the current axiosInstance or creates a new one if not existing.
 * This mechanism is shared by both client and server side. They do not, however,
 * share the instance of the client.
 * This method can be called directly from anywhere to fetch an initialized instance.
 *
 * @returns the current HTTP Client (axios) instance
 */
export const getClient = (): AxiosInstance => {
  if (!axiosInstance) {
    throw new Error('Http Client has not been initialized yet !');
  }
  return axiosInstance;
};

/**
 * Adds Authorization header to requests going to /web/ endpoints
 * if a JWT token is available in cookies
 */
export const addAuthorizationHeader = (
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig => {
  if (config.url?.includes('/web/')) {
    const jwt = getJwtFromCookie();

    if (jwt) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${jwt}`;
    }
  }

  return config;
};

/**
 * Initializes an HTTP Client with the given settings.
 * After being initialized, the client is stored for all subsequent calls.
 *
 * @param settings the settings to initialize the HTTP Client with
 * @return the newly initialized client.
 */
export const initializeHttpClient = (settings?: ClientSettings) => {
  const { headers, ...otherOptions } = settings?.options || {};

  const httpAgent = new http.Agent({ keepAlive: true });
  const httpsAgent = new https.Agent({ keepAlive: true });

  axiosInstance = axios.create({
    ...otherOptions,
    headers: {
      app_name: 'GlossGenius',
      ...headers,
    },
    httpAgent: httpAgent,
    httpsAgent: httpsAgent,
  });

  axiosInstance.interceptors.request.use(addAuthorizationHeader, (error) => {
    return Promise.reject(error);
  });

  return axiosInstance;
};
